import { ChangeEvent, useContext, useEffect } from 'react';
import { validateFormField } from '../Core/FormFieldValidation';
import { SmartContext } from '../Core/SmartContext';
import { evaluateExpression, getControlValueFromState, handleControlValueChange } from '../Core/SmartFunctions';
import { SimpleFormControlArguments, State } from '../Core/SmartTypes';
import ErrorControl from './ErrorControl';

const TimeInputControl = (args: SimpleFormControlArguments) => {
    const { state, dispatch } = useContext(SmartContext);
    const { control, dataKey, parentDataKey } = { ...args };
    const data = (getControlValueFromState(dataKey, state as State) as string) || '';

    const readOnly =
        control.readOnly ||
        evaluateExpression(control.readOnlyExpression, state?.data, getControlValueFromState(parentDataKey as string, state as State));

    useEffect(() => {
        const errorMessages = validateFormField(
            control,
            data,
            state,
            control?.props?.label,
            dataKey,
            getControlValueFromState(parentDataKey as string, state as State)
        );
        dispatch({ type: 'SET_FIELD_VALIDATION_ERRORS', payload: { dataKey, errorMessages } });
    }, []);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        // Strip all non-digits, limit to 4 digits (MM + SS)
        const digits = e.target.value.replace(/\D/g, '').slice(0, 4);

        let formatted = digits;
        if (digits.length > 2) {
            const mm = digits.slice(0, 2);
            const ssRaw = digits.slice(2);
            // Cap seconds at 59 only when both digits are entered
            const ss = ssRaw.length === 2
                ? Math.min(parseInt(ssRaw, 10), 59).toString().padStart(2, '0')
                : ssRaw;
            formatted = mm + ':' + ss;
        }

        handleControlValueChange({ control, value: formatted, dataKey, parentDataKey, state, dispatch });
    };

    const handleBlur = () => {
        if (!data) return;
        let errors: string[] = [];
        if (!/^\d{2}:[0-5]\d$/.test(data)) {
            errors = ['Enter a valid duration in MM:SS format (e.g. 05:30).'];
        } else {
            const [mm, ss] = data.split(':').map(Number);
            if (mm === 0 && ss === 0) {
                errors = ['Duration cannot be 00:00. Please enter a valid time.'];
            }
        }
        dispatch({ type: 'SET_FIELD_VALIDATION_ERRORS', payload: { dataKey, errorMessages: errors } });
    };

    return (
        <>
            {control.props?.label && (
                <label htmlFor={control.id} className="form-label m-0 mb-1 font-16 font-500 w-100">
                    {control.props.label}
                </label>
            )}
            <input
                id={control.id}
                type="text"
                className="form-control"
                placeholder="MM:SS"
                value={data}
                disabled={readOnly ?? false}
                required={!!control.required}
                maxLength={5}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <ErrorControl errorMessages={state?.formValidationErrors[dataKey]} />
        </>
    );
};

export default TimeInputControl;
